import React, { useState } from 'react';
import { Tooltip } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import {
	Combobox,
	ComboboxInput,
	ComboboxContent,
	ComboboxList,
	ComboboxItem,
	ComboboxEmpty,
} from '@hydrodigital/combobox';
import type { ComboboxOption } from '@hydrodigital/combobox';

export interface SelectWithActionOption {
	label: React.ReactNode;
	value: string | number;
	data: any;
	disabled?: boolean;
}

export interface SelectWithActionProps {
	options: SelectWithActionOption[];
	onAction?: (value: any) => void;
	tooltip?: string;
	showActionIcon?: boolean;
	icon?: React.ReactNode;
	value?: string;
	placeholder?: string;
	onChange?: (value: string) => void;
	allowClear?: boolean;
	onClear?: () => void;
	size?: 'default' | 'small';
}

const SelectWithAction: React.FC<SelectWithActionProps> = ({
	options,
	onAction,
	tooltip = 'Delete',
	showActionIcon = true,
	icon = <DeleteOutlined style={{ color: '#ff4d4f' }} />,
	value,
	placeholder,
	onChange,
	allowClear = false,
	onClear,
	size = 'small',
}) => {
	const [hoveredItem, setHoveredItem] = useState<string | number | null>(null);

	const comboboxItems: ComboboxOption[] = options.map((option) => ({
		value: String(option.value),
		label: typeof option.label === 'string' ? option.label : String(option.label),
		disabled: option.disabled,
	}));

	const handleValueChange = (newValue: string) => {
		if (newValue === '') {
			onClear?.();
		} else {
			onChange?.(newValue);
		}
	};

	return (
		<Combobox
			items={comboboxItems}
			value={value ?? ''}
			onValueChange={handleValueChange}
			size={size}
			searchable={false}
		>
			<ComboboxInput placeholder={placeholder} showClear={allowClear} />
			<ComboboxContent>
				<ComboboxEmpty>No options found.</ComboboxEmpty>
				<ComboboxList>
					{(item) => (
						<ComboboxItem
							key={item.value}
							value={item}
						>
							<div
								style={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
									width: '100%',
								}}
								onMouseEnter={() => setHoveredItem(item.value)}
								onMouseLeave={() => setHoveredItem(null)}
							>
								<span>{item.label}</span>
								{showActionIcon &&
									hoveredItem === item.value &&
									item.value !== value && (
										<Tooltip title={tooltip}>
											<span>
												<button
													type="button"
													style={{
														cursor: 'pointer',
														background: 'none',
														border: 'none',
														padding: 0,
														display: 'flex',
														alignItems: 'center',
													}}
													onClick={(e) => {
														e.stopPropagation();
														onAction?.(item.value);
													}}
												>
													{icon}
												</button>
											</span>
										</Tooltip>
									)}
							</div>
						</ComboboxItem>
					)}
				</ComboboxList>
			</ComboboxContent>
		</Combobox>
	);
};

export default SelectWithAction;
